'use client'

import { useState, useEffect } from 'react'
import { Languages, Check, Circle, Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter, usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { GlobalSidebar } from '@/components/ui/global-sidebar'

interface LanguageSelectorProps {
  className?: string
}

interface Language {
  code: string
  name: string
  nativeName: string
}

export function LanguageSelector({ className }: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [languages, setLanguages] = useState<Language[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const locale = useLocale()
  const t = useTranslations('common')
  const router = useRouter()
  const pathname = usePathname()

  // Fetch available languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch('/api/languages')
        if (response.ok) {
          const data = await response.json()
          setLanguages(data.languages || [])
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error)
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      fetchLanguages()
      setSearchQuery('') // Reset search when opening
    }
  }, [isOpen])

  const handleLanguageChange = async (langCode: string) => {
    // Set cookie for locale preference
    document.cookie = `NEXT_LOCALE=${langCode};path=/;max-age=31536000`
    
    // Auto-switch font for Arabic/RTL languages
    const rtlLanguages = ['ar', 'ur', 'fa', 'he']
    const arabicFonts = ['Cairo', 'Tajawal', 'Noto Naskh Arabic', 'Amiri', 'Almarai', 'IBM Plex Sans Arabic', 'Lateef', 'Rakkas']
    
    if (rtlLanguages.includes(langCode)) {
      // Get current preferences or create new object
      let preferences: any = {}
      try {
        const stored = localStorage.getItem('oxigen-theme-preferences')
        if (stored) {
          preferences = JSON.parse(stored)
        }
      } catch (e) {
        console.error('Failed to parse theme preferences:', e)
      }
      
      // SAVE current English font before switching (if it's not an Arabic font)
      if (preferences.fontFamily && !arabicFonts.includes(preferences.fontFamily)) {
        preferences.lastLtrFont = preferences.fontFamily
      } else if (!preferences.lastLtrFont) {
        // If no last LTR font saved, default to Inter
        preferences.lastLtrFont = 'Inter'
      }
      
      // Set Arabic font based on language
      if (langCode === 'ar') {
        preferences.fontFamily = 'Cairo'
        document.documentElement.style.setProperty('--font-family', 'Cairo')
      } else if (langCode === 'ur') {
        preferences.fontFamily = 'Noto Naskh Arabic'
        document.documentElement.style.setProperty('--font-family', 'Noto Naskh Arabic')
      } else if (langCode === 'fa') {
        preferences.fontFamily = 'Amiri'
        document.documentElement.style.setProperty('--font-family', 'Amiri')
      } else if (langCode === 'he') {
        preferences.fontFamily = 'Almarai'
        document.documentElement.style.setProperty('--font-family', 'Almarai')
      }
      
      // Save updated preferences
      localStorage.setItem('oxigen-theme-preferences', JSON.stringify(preferences))
    } else {
      // Switching back to LTR - restore previous English font
      try {
        const stored = localStorage.getItem('oxigen-theme-preferences')
        if (stored) {
          const preferences = JSON.parse(stored)
          
          // If current font is Arabic, restore the last LTR font
          if (arabicFonts.includes(preferences.fontFamily)) {
            const restoredFont = preferences.lastLtrFont || 'Inter'
            preferences.fontFamily = restoredFont
            document.documentElement.style.setProperty('--font-family', restoredFont)
            localStorage.setItem('oxigen-theme-preferences', JSON.stringify(preferences))
          }
        }
      } catch (e) {
        console.error('Failed to restore font:', e)
      }
    }
    
    // Refresh the page to apply new locale
    window.location.reload()
    
    setIsOpen(false)
  }

  // Filter languages based on search
  const filteredLanguages = languages.filter(lang =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Get current language info
  const currentLang = languages.find(lang => lang.code === locale)

  return (
    <>
      {/* Language Icon Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className={className}
        title={`Change Language (Current: ${currentLang?.nativeName || locale})`}
      >
        <Languages className="h-5 w-5" />
      </Button>

      {/* Language Sidebar using GlobalSidebar */}
      <GlobalSidebar
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('language')}
        subtitle={loading ? 'Loading...' : `${languages.length} languages available`}
        icon={<Languages className="w-5 h-5" />}
        width="md"
      >
        {/* Search Input */}
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('search') + '...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10"
          />
        </div>

        {/* Results count */}
        {searchQuery && (
          <p className="text-xs text-muted-foreground mb-4">
            {filteredLanguages.length} {filteredLanguages.length === 1 ? 'language' : 'languages'} found
          </p>
        )}

        {/* Language List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          </div>
        ) : filteredLanguages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No languages found</p>
            <p className="text-sm">Try a different search term</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredLanguages.map((language) => {
              const isSelected = locale === language.code
            
              return (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    "hover:bg-muted border border-transparent"
                  )}
                >
                  {/* Icon - Selected or Default */}
                  {isSelected ? (
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground/40 flex-shrink-0" />
                  )}
                  
                  <div className="flex-1 text-left">
                    <div className="font-medium text-base text-foreground">
                      {language.nativeName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language.name} • {language.code}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </GlobalSidebar>
    </>
  )
}
